import { X, MapPin, Navigation, Copy, ExternalLink, Share2 } from 'lucide-react'
import { useState, useEffect } from 'react'

interface MapModalProps {
  isOpen: boolean
  onClose: () => void
  location: {
    address?: string
    city?: string
    state?: string
    country?: string
    latitude?: number
    longitude?: number
    tier1?: string
    tier2?: string
  }
  postInfo?: {
    onRecordCode?: string
    timestamp?: string
    username?: string
  }
}

const MAPS_KEY = 'AIzaSyCm3hn1y-u2Ay10TUEZd0-Nlw5nQHBTdfw'

export default function MapModal({ isOpen, onClose, location, postInfo }: MapModalProps) {
  const [copied, setCopied] = useState(false)

  const { city, state, latitude, longitude } = location

  const locationName = city
    ? state ? `${city}, ${state}` : city
    : location.address || 'Location'

  const hasCoords = latitude && longitude

  const embedUrl = hasCoords
    ? `https://www.google.com/maps/embed/v1/place?key=${MAPS_KEY}&q=${latitude},${longitude}&zoom=15`
    : null

  const mapsUrl = hasCoords
    ? `https://www.google.com/maps?q=${latitude},${longitude}`
    : city
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationName)}`
    : null

  const handleCopy = () => {
    const text = hasCoords ? `${latitude}, ${longitude}` : locationName
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Handle ESC + body scroll
  useEffect(() => {
    if (!isOpen) return
    document.body.style.overflow = 'hidden'
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.body.style.overflow = 'unset'
      document.removeEventListener('keydown', handleEsc)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleShare = async () => {
    const shareText = `${locationName}${postInfo?.onRecordCode ? ` — Code: ${postInfo.onRecordCode}` : ''}`
    const shareUrl = mapsUrl || ''

    if (navigator.share) {
      try {
        await navigator.share({ title: 'Content Origin — OnRecord', text: shareText, url: shareUrl })
      } catch {}
    } else {
      navigator.clipboard.writeText(`${shareText}\n${shareUrl}`)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full mx-4 overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Google Maps — tall like mobile (70% of modal height) */}
        <div className="relative h-[60vh] bg-gray-900">
          {embedUrl ? (
            <iframe
              src={embedUrl}
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
              <MapPin className="w-12 h-12 text-onrecord-orange" />
            </div>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 left-3 w-9 h-9 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Top-right controls */}
          <div className="absolute top-3 right-3 flex gap-2 z-10">
            <button
              onClick={() => mapsUrl && window.open(mapsUrl, '_blank')}
              className="w-9 h-9 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70"
            >
              <Navigation className="w-4 h-4" />
            </button>
            <button
              onClick={handleShare}
              className="w-9 h-9 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70"
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content Origin card */}
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-5 h-5 text-gray-700" />
                <span className="text-lg font-bold text-gray-900">Content Origin</span>
              </div>
            </div>
            <span className="px-3 py-1 bg-onrecord-orange text-white text-xs font-bold rounded-full">
              OnRecord
            </span>
          </div>

          <p className="text-base text-gray-800 mt-3">{locationName}</p>

          {postInfo?.onRecordCode && (
            <p className="text-sm text-gray-500 mt-1">
              Code: <span className="text-onrecord-orange font-mono">{postInfo.onRecordCode}</span>
            </p>
          )}

          {postInfo?.timestamp && (
            <p className="text-sm text-gray-400 mt-1">
              {new Date(postInfo.timestamp).toLocaleDateString('en-IN', {
                day: 'numeric', month: 'numeric', year: 'numeric',
              })}, {new Date(postInfo.timestamp).toLocaleTimeString('en-IN', {
                hour: 'numeric', minute: '2-digit', hour12: true,
              })}
            </p>
          )}
        </div>

        {/* Buttons */}
        <div className="px-4 pb-4 flex gap-3">
          <button
            onClick={handleCopy}
            className="flex-1 flex items-center justify-center gap-2 py-3 border border-gray-300 rounded-full text-sm font-semibold text-onrecord-orange hover:bg-orange-50 transition-colors"
          >
            <Copy className="w-4 h-4" />
            {copied ? 'Copied!' : 'Explore More'}
          </button>

          {mapsUrl && (
            <button
              onClick={() => window.open(mapsUrl, '_blank')}
              className="flex-1 flex items-center justify-center gap-2 py-3 border border-gray-300 rounded-full text-sm font-semibold text-onrecord-orange hover:bg-orange-50 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Open In
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
